//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import {
  DEFAULT_SIZE,
  LG_SIZE,
  SM_SIZE,
} from '@components/atoms/icon/iconConstants';
import { iconMap } from '@components/atoms/icon/iconMap';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface Props extends ITrackableComponent {
  className?: string;
  // Using iconSize rather than size to prevent conflicts with other components
  iconSize?: 'sm' | 'md' | 'lg';
  name: UnisonIconType;
}

/**
 * The Icon atom renders an SVG icon that will inherit the current text color of its container.
 *
 * Icons are compiled from SVG files into React components at build time via @svgr/webpack,
 * eliminating the runtime fetch and parsing that react-inlinesvg previously performed.
 *
 * The icon size can be set via the `iconSize` prop: 'sm' (12px), 'md' (16px), or 'lg' (24px).
 * If no size is specified, it defaults to 16px.
 *
 * The Icon can be styled directly via styled-components.
 */
export const Icon = ({ className, iconSize, name, trackingId }: Props) => {
  const size = (() => {
    if (iconSize === 'sm') {
      return SM_SIZE;
    }
    if (iconSize === 'lg') {
      return LG_SIZE;
    }
    return DEFAULT_SIZE;
  })();

  const SvgIcon = iconMap[name];

  if (!SvgIcon) {
    console.warn(`Icon "${name}" not found in icon map`);
    return null;
  }

  return (
    <SvgWrapper
      $size={size}
      aria-hidden={true}
      className={className}
      trackingId={trackingId}
    >
      <SvgIcon focusable={false} />
    </SvgWrapper>
  );
};

const SvgWrapper = withTracking(
  styled.span<{ $size: number }>(
    ({ $size }) => `
      /** Ensures that icons don't unintentionally resize inside of flex containers */
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: ${$size}px;
      height: ${$size}px;

      > svg {
        width: 100%;
        height: 100%;
        /** Sets the icon to the same color as the current font color */
        fill: currentColor;
        /** Required for MS Edge to prevent clicks on SVG elements */
        pointer-events: none;
      }
    `,
  ),
);

export default Icon;
